import { Button } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { getItemPOList, getItemPRList } from '../../api';
import StyledButton from '../../components/Common/StyledButton';
import { AmountCell } from '../../components/CustomComponents/AmountCell';
import DateFilter from '../../components/CustomComponents/DateFilter';
import { setBreadcrumbItems } from '../../store/actions';
import Table from '../DummyTable/Table';
import './styles/reports.scss';
import useAuth from '../../hooks/useAuth';
import SingleSelect from '../DummyTable/SingleSelect';
import RequireUserType from '../../routes/middleware/requireUserType';
import { MODULES_ENUM, PERMISSIONS_ENUM, USER_TYPES_ENUM } from '../../utility/constants';


const defaultPageSize = 10;

const getDefaultYearDateRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    start.setHours(0, 0, 0, 0);

    const end = new Date(now);
    end.setHours(23, 59, 59, 999);

    return {
        start: start.toISOString(),
        end: end.toISOString(),
    };
};
const reportTypeList = [
    { id: 'PO', name: 'PO' },
    { id: 'PR', name: 'PR' }
]

const getColumns = (reportType, userType) => {
    const isAdmin = userType?.toLowerCase() === "admin";



    return [
        {
            accessorKey: isAdmin ? 'itemName' : 'alternateItemTitle',
            header: () => <span>Item Name</span>,
            defaultSortIcon: true,
            enableSorting: true,
            cell: (value) => <span>{value.getValue() || '-'}</span>,
        },
        {
            accessorKey: 'alternateItemTitle',
            header: () => <span>Description</span>,
            cell: (value) => <span>{value.getValue() || '-'}</span>,
        },
        {
            accessorKey: 'itemUnit',
            header: () => <span>Unit</span>,
            cell: (value) => <span>{value.getValue() || '-'}</span>,
        },
        {
            accessorKey: 'totalQuantity',
            header: () => <span>Total Qty</span>,
            cell: (value) => <span>{value.getValue() || 0}</span>,
        },
        {
            accessorKey: 'totalAmount',
            header: () => <span>Subtotal</span>,
            cell: ({ getValue }) => <AmountCell value={getValue()} />,
        },
        {
            accessorKey: 'taxPercentage',
            header: () => <span>GST (%)</span>,
            cell: (value) => (<span>{Number(value.getValue())}%</span>),
        },
        {
            accessorKey: 'totalTax',
            header: () => <span>GST Amount</span>,
            cell: ({ getValue }) => <AmountCell value={getValue()} />,

        },
        {
            accessorKey: 'grandTotal',
            header: () => <span>Total</span>,
            cell: ({ getValue }) => <AmountCell value={getValue()} />,
        },
    ];
};


const ByPRPO = (props) => {
    const [searchParams,setSearchParams]=useSearchParams()
    const reportType = searchParams.get('reportType') 
    const startDateParam=searchParams.get('startDate')
    const endDateParam=searchParams.get('endDate')
    const {auth}=useAuth()
    const { userType, reportMode } = props;
    const [sorting, setSorting] = useState(()=>{
        const sortField=searchParams.get('sortField')
        const sortOrder=searchParams.get('sortOrder')

        return [
            {
                id:sortField||'grandTotal',
                desc:sortOrder?sortOrder==='desc':true
            }
        ]
    });
    const [pagination, setPagination] = useState(()=>({
        pageIndex:Math.max(Number(searchParams.get('page'))-1,0),
        pageSize: Number(searchParams.get('limit'))||defaultPageSize,
    }));
    const [total, setTotal] = useState(0);
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const defaultDateRange = useRef(getDefaultYearDateRange());
    const [date, setDate] = useState(() => defaultDateRange.current);
    const [isExporting, setExporting] = useState(false);
    const skipDateEffect = useRef(true);
    const { selectedWarehouses, selectedBranches } = useAuth()

    const effectCalled = useRef(false);

    const breadcrumbItems =
        [
            { title: 'Dashboard', link: '/dashboard' },
            { title: 'By Item Summary', link: '#' },
        ]


    useEffect(() => {
        if (!effectCalled.current) {
            props.setBreadcrumbItems(
                'By Item Summary',
                breadcrumbItems,
            );
            effectCalled.current = true;
        }
    }, []);

    const getItemPRPOList = async (page, limit, isExport = false) => {
        const sort = sorting[0] || { id: 'totalQuantity', desc: true };

        const dateRange = date || defaultDateRange.current;

        const body = {
            page: isExport ? 1 : page,
            limit: isExport ? 1000000 : limit,
            sortOrder: sort.desc ? 'desc' : 'asc',
            sortBy: sort.id || 'totalQuantity',
            clientId:
                userType === 'client'
                    ? JSON.parse(localStorage.getItem('user'))?.clientId
                    : '',
            startDate: dateRange.start,
            endDate: dateRange.end,
            warehouseId: selectedWarehouses,
            branchId: selectedBranches
        };

        return reportType === 'PO'
            ? await getItemPOList(body)
            : await getItemPRList(body);
    };

    const fetchData = async (isExport = false) => {
        try {
            if (isExport) {
                setExporting(true);
            } else {
                setIsLoading(true);
            }

            const res = await getItemPRPOList(
                pagination.pageIndex + 1,
                pagination.pageSize,
                isExport,
            );

            if (res.success) {
                if (isExport) {
                    return res.payload.data;
                } else {
                    setRows(res.payload.data);
                    setTotal(res.payload.totalCount);
                }
            }
            return [];
        } catch (err) {
            console.error(err);
            if (isExport) {
                toast.error('Failed to fetch data for export');
            }
            return [];
        } finally {
            if (isExport) {
                setExporting(false);
            } else {
                setIsLoading(false);
            }
        }
    };
    useEffect(()=>{
        setSearchParams((prev)=>{
            const params=new URLSearchParams(prev)
            params.set('page',String(pagination.pageIndex+1))
            params.set('limit',String(pagination.pageSize))

            return params

        },{replace:true})
    },[pagination,searchParams])
    
    useEffect(()=>{
        const sort=sorting?.[0];
        if(!sort)
        {
            return;
        }
        setSearchParams((prev)=>{
            const params=new URLSearchParams(prev)
            params.set('sortField',sort.id)
            params.set('sortOrder',sort.desc?'desc':'asc');

            return params

        },{replace:true})
    },[sorting,setSearchParams])

    useEffect(() => {
        if (skipDateEffect.current) {
            skipDateEffect.current = false;
            return;
        }

        fetchData(false);
    }, [pagination, sorting, date, selectedWarehouses, selectedBranches, reportType]);

    const columns = useMemo(() => getColumns(reportType, userType), [reportType]);
    const clientUserColumnDefs = columns.filter(colDef => colDef.accessorKey!== "totalAmount"&&colDef.accessorKey!=="taxPercentage"&&colDef.accessorKey!=="totalTax"&&colDef.accessorKey!=="grandTotal")

    const resetPagination = () => {
        setPagination({
            pageIndex: 0,
            pageSize: defaultPageSize,
        });
    };

    const resetFilters = () => {
        setDate(defaultDateRange.current);
        setSorting([{ id: 'totalQuantity', desc: true }]);
        setSearchParams({ reportType: 'PR' }, { replace: true });
        resetPagination();
    };

    const onDateChange = (data) => {
        skipDateEffect.current = false;
        setDate(data);
    };

    const downloadItemsExcel = async () => {
        try {
            const data = await fetchData(true);
            const isAdmin = userType === "admin"
            if (data.length > 0) {
            let exportColumns;

            if (userType === "admin") {
            exportColumns = columns;
            } else if (
            auth?.permissions?.some(
            (p) =>
                p.module === MODULES_ENUM.CLIENT_REPORTS &&
                [PERMISSIONS_ENUM.READ, PERMISSIONS_ENUM.CREATE].every((perm) =>
                p.operations.includes(perm)
                )
            )
            ) {
                exportColumns = columns;
            } else {
            exportColumns = clientUserColumnDefs;
            }
            const flatItems = data.map((row) => {
            const excelRow = {};
            exportColumns.forEach((col) => {
            const header = col.header()?.props?.children || col.accessorKey;
            excelRow[header] = col.exportValue
                ? col.exportValue(row)
                : row[col.accessorKey];
            });

            return excelRow;
        });

                const worksheet = XLSX.utils.json_to_sheet(flatItems);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, 'PR_PO');

                XLSX.writeFile(
                    workbook,
                    reportType === 'PO' ? 'PO-Item-List.xlsx' : 'PR-Item-List.xlsx',
                );
                toast.success('Item(s) Exported Successfully!');
            }
        } catch (error) {
            console.error('Error downloading Excel:', error);
            toast.error('Error exporting data');
        }
    };

    return (
        <div>
            <div className="filter-wrapper">
                <div className="filter-toolbar">
                    {/* LEFT SIDE */}
                    <div className="filter-row">
                        <DateFilter
                            onDateChange={onDateChange}
                            initialValue={defaultDateRange.current}
                        />
                        <SingleSelect
                            label="Report Type"
                            list={reportTypeList}
                            selectedValue={reportType}
                            handleChange={(e) => {
                                const value = e.target.value;
                                setSearchParams({ reportType: value });
                            }}
                        />
                        <Button
                            variant="outlined"
                            className="reset-btn"
                            onClick={resetFilters}
                        >
                            Reset
                        </Button>
                    </div>

                    {/* RIGHT SIDE */}
                    <StyledButton
                        color="primary"
                        className="export-btn"
                        onClick={downloadItemsExcel}
                        isLoading={isExporting}
                        disabled={isLoading}
                    >
                        <div className="export-btn-content">
                            <i className="ion ion-md-cloud-download mx-2"></i>
                            Export
                        </div>
                    </StyledButton>
                </div>
            </div>
            <RequireUserType userType={USER_TYPES_ENUM.ADMIN}>
                <Table
                data={rows}
                columns={columns}
                manualPagination
                rowCount={total}
                sorting={sorting}
                pagination={pagination}
                setSorting={setSorting}
                setPagination={setPagination}
                isLoading={isLoading}
                defaultSorting={[{ id: 'totalQuantity', desc: true }]}
            />
            </RequireUserType>
            <RequireUserType userType={USER_TYPES_ENUM.CLIENT}>
                <Table
                data={rows}
                columns={auth?.permissions?.some(
                          (p) =>
                            p.module === MODULES_ENUM.CLIENT_REPORTS &&
                            [PERMISSIONS_ENUM.READ, PERMISSIONS_ENUM.CREATE].every((perm) => p.operations.includes(perm))
                            )
                        ? columns:auth?.permissions?.some(
                          (p) =>
                            p.module === MODULES_ENUM.CLIENT_REPORTS && p.operations.includes(PERMISSIONS_ENUM.READ)
                          )? clientUserColumnDefs: []}
                manualPagination
                rowCount={total}
                sorting={sorting}
                pagination={pagination}
                setSorting={setSorting}
                setPagination={setPagination}
                isLoading={isLoading}
                defaultSorting={[{ id: 'totalQuantity', desc: true }]}
            />
            </RequireUserType>
        </div>
    );
};

export default connect(null, { setBreadcrumbItems })(ByPRPO);
